import React, { useState, useMemo } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "./ui/sonner";
import {
  PRELIMINARY_PROFILES,
  INDEPTH_PROFILES,
  suggestProfile,
} from "../demo/demoProfiles";
import { useAdminDemoMode, logAdminDemoEvent } from "../hooks/useAdminDemoMode";

/*
 * DemoAutoCompleteBar — admin-only control bar rendered above the existing
 * "Complete Assessment" button on the assessment-taking page. Hidden from
 * the DOM entirely unless:
 *   • the authenticated user's role is super_admin / consultant / admin
 *   • the consultant has actively flipped Demo Mode on in the admin sidebar
 *
 * Props:
 *   session          — auth session, used for role gating + audit logs
 *   assessmentType   — "initial" | "detailed"  (drives profile bank)
 *   prospect         — { industry, employee_count, ai_maturity_hint }, used by
 *                       the "Match the prospect" auto-pick option
 *   questions        — array of question objects from the API
 *   onApplyComplete  — async callback: (responses, profileKey) => Promise<void>
 *                       Implementer fills DB + transitions to complete.
 */
const PROFILE_OPTIONS = [
  { value: "auto",   label: "Match the prospect's story" },
  { value: "low",    label: "Low AI Proficiency" },
  { value: "medium", label: "Medium AI Proficiency" },
  { value: "high",   label: "High AI Proficiency" },
];

export const DemoAutoCompleteBar = ({
  session,
  assessmentType = "initial",
  prospect = {},
  questions = [],
  onApplyComplete,
}) => {
  const { eligible, on } = useAdminDemoMode(session);
  const [profileKey, setProfileKey] = useState("auto");
  const [running, setRunning] = useState(false);

  const profileBank = assessmentType === "detailed" ? INDEPTH_PROFILES : PRELIMINARY_PROFILES;

  const resolvedProfileKey = useMemo(() => {
    if (profileKey !== "auto") return profileKey;
    return suggestProfile({
      industry: prospect?.industry,
      employee_count: prospect?.employee_count,
      ai_maturity_hint: prospect?.ai_maturity_hint,
    });
  }, [profileKey, prospect]);

  // Eligibility gate at RENDER time — never just CSS-hide.
  if (!eligible || !on) return null;

  const buildResponses = () => {
    const profile = profileBank[resolvedProfileKey];
    if (!profile) return [];

    return questions.map((q) => {
      const rtype = q.response_type;
      const opts = q.answer_options || [];

      // Helper — choose option by value, fall back to label substring, fall back to first option
      const pickOption = (preferredValue, fallbackLabelHint) => {
        if (!opts.length) return null;
        let hit = opts.find((o) => String(o.value) === String(preferredValue));
        if (!hit && fallbackLabelHint) {
          const rx = new RegExp(fallbackLabelHint, "i");
          hit = opts.find((o) => rx.test(o.label || ""));
        }
        if (!hit) {
          // eslint-disable-next-line no-console
          console.warn(`[DEMO] No exact option match on q ${q.id}, using first option`, { q, preferredValue });
          hit = opts[0];
        }
        return hit;
      };

      let value;
      if (rtype === "single_select" || rtype === "multi_select") {
        // Y/N detection — answer_options length 2 with value 'yes' or 'no'
        const isYesNo = opts.length === 2 &&
          opts.every((o) => /^(yes|no)$/i.test(String(o.value)));
        if (isYesNo) {
          const target = profile.yes_no || "yes";
          value = pickOption(target, target)?.value ?? opts[0]?.value;
        } else {
          // Scored / multi-choice — try numeric scale match
          value = pickOption(profile.answer_value, profile.answer_value)?.value ?? opts[0]?.value;
        }
        if (rtype === "multi_select") value = [value];
      } else if (rtype === "long_text") {
        value = profile.long_text;
      } else if (rtype === "short_text") {
        value = profile.short_text;
      } else if (rtype === "numeric_scale") {
        value = profile.answer_value;
      } else {
        // Unknown type — leave blank
        value = "";
      }

      return { question_id: q.id, response_value: value };
    });
  };

  const handleApply = async () => {
    if (!questions.length) {
      toast.error("No questions loaded to auto-fill");
      return;
    }
    setRunning(true);
    const responses = buildResponses();
    try {
      logAdminDemoEvent({
        feature: `assessment_auto_complete_${assessmentType}`,
        session,
        extra: {
          profile: resolvedProfileKey,
          question_count: responses.length,
          prospect_industry: prospect?.industry || null,
        },
      });
      await onApplyComplete(responses, resolvedProfileKey);
      toast.success(`Auto-filled ${responses.length} questions with "${profileBank[resolvedProfileKey]?.label}" profile`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[DEMO] Auto-complete failed", err);
      toast.error("Auto-complete failed — see console");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="mt-8 pt-5 border-t border-dashed border-[#e5e7eb]"
      data-testid="demo-autocomplete-bar"
    >
      <div className="rounded-xl border border-[#bfe3ee] bg-[#f0f9fc] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 shrink-0">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#007a9e] text-white">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="text-[13px] font-semibold text-[#00485f] tracking-wide uppercase">
            Demo: Auto-Complete Assessment
          </span>
        </div>
        <div className="flex-1 flex flex-col sm:flex-row gap-2 sm:items-center">
          <select
            value={profileKey}
            onChange={(e) => setProfileKey(e.target.value)}
            className="flex-1 min-w-[200px] rounded-md border border-[#bfe3ee] bg-white text-[#00485f] text-[13px] font-medium px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#007a9e]"
            data-testid="demo-profile-select"
            disabled={running}
          >
            {PROFILE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
                {o.value === "auto" && resolvedProfileKey ? ` → ${profileBank[resolvedProfileKey]?.label || resolvedProfileKey}` : ""}
              </option>
            ))}
          </select>
          <Button
            onClick={handleApply}
            disabled={running}
            className="bg-[#1A1F3C] hover:bg-[#0f1226] text-white text-[13px] font-semibold px-4 h-9 rounded-md shadow-none"
            data-testid="demo-apply-complete-btn"
          >
            {running ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying…</>
            ) : (
              <>Apply &amp; Complete</>
            )}
          </Button>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-[#9ca3af] italic">
        Admin demo utility. Submissions are tagged <code>demo_session=true</code> and excluded from analytics.
      </p>
    </div>
  );
};

export default DemoAutoCompleteBar;
